import { ConfirmDialog } from '@/_shared/components/ConfirmDialog';

interface DeleteActivationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the activation ships INTO — named, because "this activation" is not a thing anyone recognises. */
  destinationLabel: string;
  loading?: boolean;
  onConfirm: () => void;
}

/**
 * The one confirmation for deleting an activation, shared by the list row's ⋯ menu and the detail page.
 *
 * It exists as a component rather than two call sites of {@link ConfirmDialog} because of the SECOND
 * paragraph, which is the whole point and the easiest thing to lose in a copy-paste: deleting an
 * activation removes the RudderStack connection and destination, and does NOT remove the audience
 * already built on the far side. That audience stops updating but stays targetable — so someone
 * deleting this to stop advertising to those people would otherwise walk away believing they had.
 *
 * A generic "this can't be undone" would be true and useless. The thing that cannot be undone from
 * here is the part we do not control.
 */
export function DeleteActivationDialog({
  open,
  onOpenChange,
  destinationLabel,
  loading,
  onConfirm,
}: DeleteActivationDialogProps): React.ReactElement {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      loading={loading}
      title="Delete this activation?"
      description={
        <>
          Lookout will stop syncing to <strong>{destinationLabel}</strong> and remove the connection.
          <br />
          <br />
          The audience already in the destination is <strong>not deleted</strong> — it stops updating but remains usable
          there. Remove it in the destination if you no longer want it used.
        </>
      }
      confirmLabel="Delete activation"
      onConfirm={onConfirm}
    />
  );
}
